import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import {
  deleteLeadAttachment,
  downloadAttachment,
  isLocalAttachment,
  listBusinessAttachments,
  listLeadAttachments,
  uploadLeadAttachment,
} from '../lib/resources';
import type { Attachment } from '../types';
import { Button, ErrorNote, Loading } from './ui';

const ICONS: Record<Attachment['type'], string> = {
  IMAGE: '🖼️',
  VIDEO: '🎬',
  AUDIO: '🎧',
  FILE: '📄',
};

const formatSize = (bytes: number): string => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Aba Arquivos do card: anexos do lead, com upload e download.
 * O anexo local é servido pela API e exige o Bearer, então o download passa
 * pelo cliente HTTP e não por um `<a href>` puro.
 */
export function BusinessFiles({ leadId, businessId }: { leadId: string; businessId?: string }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // No negócio, só os anexos dele; na ficha do lead, todos.
  const attachmentsQuery = useQuery({
    queryKey: ['attachments', leadId, businessId ?? 'all'],
    queryFn: () =>
      businessId ? listBusinessAttachments(businessId) : listLeadAttachments(leadId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['attachments', leadId] });

  const upload = useMutation({
    mutationFn: (file: File) => uploadLeadAttachment(leadId, file, { businessId }),
    onSuccess: () => {
      if (fileInput.current) fileInput.current.value = '';
      invalidate();
    },
    onError: (mutationError) =>
      setError(errorMessage(mutationError, 'Não foi possível enviar o arquivo')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteLeadAttachment(leadId, id),
    onSuccess: invalidate,
  });

  const download = useMutation({
    mutationFn: (attachment: Attachment) => downloadAttachment(attachment),
    onError: (mutationError) =>
      setError(errorMessage(mutationError, 'Não foi possível baixar o arquivo')),
  });

  const attachments = attachmentsQuery.data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <input
          ref={fileInput}
          type="file"
          className="text-sm text-slate-600 file:mr-3 file:rounded-lg file:border file:border-slate-300 file:bg-white file:px-3 file:py-1.5 file:text-sm"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              setError(null);
              upload.mutate(file);
            }
          }}
        />
        {upload.isPending && <span className="text-xs text-slate-500">Enviando…</span>}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {attachmentsQuery.isLoading ? (
        <Loading />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="divide-y divide-slate-100">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span aria-hidden className="text-lg">
                    {ICONS[attachment.type]}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">
                      {attachment.fileName}
                    </p>
                    <p className="text-xs text-slate-400">
                      {formatSize(attachment.size)} · {formatDateTime(attachment.createdAt)}
                      {attachment.businessId === businessId && businessId
                        ? ' · deste negócio'
                        : ''}
                      {!isLocalAttachment(attachment) ? ' · link externo' : ''}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    onClick={() => download.mutate(attachment)}
                    disabled={download.isPending}
                    className="px-2 py-1 text-xs"
                  >
                    Baixar
                  </Button>
                  <button
                    type="button"
                    onClick={() => remove.mutate(attachment.id)}
                    className="text-xs text-slate-400 hover:text-red-600"
                  >
                    excluir
                  </button>
                </div>
              </div>
            ))}

            {attachments.length === 0 && (
              <p className="px-4 py-8 text-center text-xs text-slate-400">
                Nenhum arquivo anexado a este lead.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
